import type { Expression } from 'mapbox-gl';
import type { CatalogEntry } from './iconCatalog';

/** Default landmark badge per registry source_feature_type (non-POI). */
export const LANDMARK_SOURCE_DEFAULT_ICONS = {
  water: 'coordal-landmark-water',
  landuse: 'coordal-landmark-landuse',
  landcover: 'coordal-landmark-landcover',
  structure: 'coordal-landmark-structure',
  transport_stop: 'coordal-landmark-transport-stop',
} as const;

/** POI landmark tile ``type`` values → catalog icon (mirrors ghana-landmark-labels style). */
export const POI_LANDMARK_TILE_ICON: Record<string, string> = {
  government: 'coordal-landmark-town-hall',
  diplomatic: 'coordal-landmark-embassy',
  townhall: 'coordal-landmark-town-hall',
  courthouse: 'coordal-landmark-courthouse',
  university: 'coordal-landmark-college',
  museum: 'coordal-landmark-museum',
  CASTLE: 'coordal-landmark-castle',
  MONUMENT: 'coordal-landmark-monument',
  attraction: 'coordal-landmark-attraction',
  viewpoint: 'coordal-landmark-viewpoint',
  artwork: 'coordal-landmark-art-gallery',
  memorial: 'coordal-landmark-monument',
  place_of_worship: 'coordal-landmark-place-of-worship',
};

/** Registry / transport icons — handled outside POI type match. */
const NON_POI_LANDMARK_ICON_IDS = new Set<string>([
  ...Object.values(LANDMARK_SOURCE_DEFAULT_ICONS),
  'coordal-landmark-castle',
  'coordal-landmark-transport-rail',
]);

const STRUCTURE_LANDMARK_ICON_MATCH: Expression = [
  'match',
  ['get', 'type'],
  'city_wall',
  'coordal-landmark-castle',
  LANDMARK_SOURCE_DEFAULT_ICONS.structure,
];

const TRANSPORT_LANDMARK_ICON_MATCH: Expression = [
  'match',
  ['get', 'type'],
  'rail_station',
  'coordal-landmark-transport-rail',
  'ferry_terminal',
  LANDMARK_SOURCE_DEFAULT_ICONS.water,
  LANDMARK_SOURCE_DEFAULT_ICONS.transport_stop,
];

function isValidMatchExpression(expr: unknown): expr is Expression {
  if (!Array.isArray(expr) || expr[0] !== 'match' || expr.length < 4) return false;
  // input + at least one label/output pair before fallback
  return expr.length >= 5;
}

/** POI-backed landmarks: match tile ``type`` to catalog entries. */
export function buildPoiLandmarkTypeMatch(entries: CatalogEntry[]): Expression {
  const branches: unknown[] = ['match', ['get', 'type']];
  let defaultId = 'coordal-landmark-star';

  for (const [tileType, iconId] of Object.entries(POI_LANDMARK_TILE_ICON)) {
    branches.push(tileType, iconId);
  }

  for (const entry of entries) {
    if (entry.sourceFeatureTypes?.length) continue;
    if (NON_POI_LANDMARK_ICON_IDS.has(entry.id)) continue;
    for (const tileType of entry.tileTypes ?? []) {
      if (tileType === '*') {
        defaultId = entry.id;
        continue;
      }
      branches.push(tileType, entry.id);
    }
  }

  branches.push(defaultId);
  const matchExpr = branches as Expression;
  if (!isValidMatchExpression(matchExpr)) {
    return defaultId as unknown as Expression;
  }
  return matchExpr;
}

/** Full ghana-landmark-labels icon-image expression (source type + POI type). */
export function buildLandmarkIconImage(entries: CatalogEntry[]): Expression {
  const poiMatch = buildPoiLandmarkTypeMatch(entries);
  return [
    'case',
    ['==', ['get', 'source_feature_type'], 'water'],
    LANDMARK_SOURCE_DEFAULT_ICONS.water,
    ['==', ['get', 'source_feature_type'], 'landuse'],
    LANDMARK_SOURCE_DEFAULT_ICONS.landuse,
    ['==', ['get', 'source_feature_type'], 'landcover'],
    LANDMARK_SOURCE_DEFAULT_ICONS.landcover,
    ['==', ['get', 'source_feature_type'], 'structure'],
    STRUCTURE_LANDMARK_ICON_MATCH,
    ['==', ['get', 'source_feature_type'], 'transport_stop'],
    TRANSPORT_LANDMARK_ICON_MATCH,
    poiMatch,
  ] as Expression;
}
